import axios from 'axios';

import route from '@H/route';
import router from '@R';

import { getErrorFromResponse } from '@H/error-codes';

/**
 * Shared verification method.
 *
 * Used for email verification on both the
 * normal enrolment and marketing journey enrolment.
 *
 * @param {String} routeType Key to use when grabbing route
 * @param {Function} $gettext Translation helper
 * @param {Function} dispatch Vuex function to dispatch an action
 * @param {Function} commit Vuex function to commit a mutation
 * @param {Object} rootState Vuex root state, allowing access to other modules
 */
export const verify = async (routeType, $gettext, { dispatch, commit, rootState }) => {

    const { route: { query } } = rootState;
    const expires = `expires=${query.expires}`;
    const id = `id=${query.id}`;
    const signature = `signature=${query.signature}`;

    let url = `${route(`${routeType}.verify`)}?${expires}&${id}&${signature}`;

    if (query.productId && query.purchaseOption) {
        // For the marketing journey we must pass the product ID and purchase option.
        const productId = `productId=${query.productId}`;
        const purchaseOption = `purchaseOption=${query.purchaseOption}`;
        let addonPrice = 'price=';
        let addonCurrency = 'currency=';

        if (typeof query.price != 'undefined' && typeof query.currency != 'undefined') {
            addonPrice = `price=${query.price}`;
            addonCurrency = `currency=${query.currency}`;
        } else if (query.price != '' && query.currency != '') {
            addonPrice = `price=${query.price}`;
            addonCurrency = `currency=${query.currency}`;
        }

        url += `&${productId}&${purchaseOption}&${addonPrice}&${addonCurrency}`;
    }

    try {
        const res = await axios.post(url, { id: rootState.route.id });
        // load translations
        await dispatch('setLanguage', res.data.user.language, {
            root: true
        });

        // set the language in the state so the language picker can auto-change
        commit('Auth/language', res.data.user.language, {
            root: true
        });

        let flash = {};

        if (res.data.verified) {
            flash = {
                message: $gettext(
                    'Your e-mail address was verified successfully, please login to continue.'
                ),
                key: 'success'
            };
        } else {
            flash = {
                message: $gettext(
                    'Something went wrong, please try again later'
                ),
                key: 'error'
            };
        }

        dispatch('Flash/setFlash', flash, { root: true });

        if (!res.data.marketingEnrolment && !res.data.verified) {
            return dispatch('Auth/logout', null, { root: true });
        }

        if (res.data.marketingEnrolment) {
            sessionStorage.setItem('isMarketing', true);
            commit('Marketing/email', res.data.user.email, { root: true });
        }

        if (res.data.mustEnrol) {
            if (res.data.hasPassword) {
                commit('hasPassword', res.data.hasPassword);
            }

            if (res.data.marketingEnrolment) {
                if (typeof res.data.addon['price'] != 'undefined' && typeof res.data.addon['currency'] != 'undefined') {
                    commit('setAddon', { price: res.data.addon['price'], currency: res.data.addon['currency'] })
                } else if (res.data.addon['price'] != '' && res.data.addon['currency'] != '') {
                    commit('setAddon', { price: res.data.addon['price'], currency: res.data.addon['currency'] })
                } else {
                    commit('setAddon', { price: '', currency: '' })
                }
            }

            return router.push({
                name: res.data.marketingEnrolment ? 'Marketing Step 1' : 'begin-enrolment'
            });
        }

        return axios.post(route('get-token')).then(res => {
            return dispatch('Auth/authenticated', {
                token: res.data.token,
                user: res.data.user
            }, { root: true });
        }).catch(() => {
            return dispatch('Auth/logout', null, { root: true });
        });
    } catch (err) {
        let flash = { message: getErrorFromResponse(err) };
        dispatch('Flash/setFlash', flash, { root: true });

        return dispatch('Auth/logout', null, { root: true });
    }
}

/**
 * Shared get user call.
 *
 * Used in guards for marketing and others.
 *
 * @param {String} type Type of module to signify what state to use
 * @param {Object} rootState Vuex root state, allowing access to other modules
 */
export const getUser = async (type, { rootState, commit, dispatch }) => {
    const user = rootState[type].user;
    let endFunction;

    if (type === 'Marketing') {
        endFunction = async res => {
            await dispatch(`${type}/setUser`, res.data.user, { root: true });
        }
    } else {
        endFunction = async res => {
            commit(`${type}/setUser`, res.data.user, { root: true });
            commit('hasUserNow', true);
        }
    }

    if (user && user.email) {
        return user;
    } else {
        try {
            const res = await axios.post(route('user'), {});

            if (!res.data.signedIn) {
                return null;
            } else {

                await endFunction(res);

                return res.data.user;
            }
        } catch (err) {
            return err;
        }
    }
}

/**
 * Handles fetching a single-use token and sending the user off to FX blue with the passed token/user_id
 * @param {String} type - Identifier for module source
 * @param {Function} dispatch - Dispatch a vuex action
 */
export const complete = async (type, { dispatch }) => {
    try {
        const res = await axios.post(route('get-token'));

        return dispatch(
            'Auth/authenticated',
            {
                token: res.data.token,
                user: res.data.user,
                session_token_url: res.data.session_token_url
            },
            { root: true }
        );
    } catch (err) {
        dispatch(
            'Flash/setFlash',
            { message: getErrorFromResponse(err) },
            { root: true }
        );
        throw err;
    }
}

/**
 * Load payment confirmation data
 * @param {String} type - Identifier for module source
 * @param {Function} $gettext Translation helper
 * @param {Function} commit - Commit a vuex mutation
 * @param {Function} dispatch - Dispatch a vuex action
 */
export const loadPaymentConfirmation = async (type, $gettext, { commit, dispatch }) => {
    try {
        const { data: { payment } } = await axios.get(route('payment-confirmation'));
        commit('payment', payment);
    } catch (err) {
        dispatch(
            'Flash/setFlash',
            $gettext('There was a problem loading payment data.'),
            { root: true }
        );
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/shared.js